(() => {
  const DESKTOP = '(min-width: 900px)';
  const MOBILE_THRESHOLD = 1;
  const HERO_GAP = 8;
  const HEADLINE_GAP = 16;
  class TISNavigation {
    constructor(root) {
      this.root = root;
      this.desktop = window.matchMedia(DESKTOP);
      this.triggers = [...root.querySelectorAll('[data-tis-menu-trigger]')];
      this.menus = [...root.querySelectorAll('[data-tis-menu]')];
      this.drawer = root.querySelector('[data-tis-drawer]');
      this.drawerToggle = root.querySelector('[data-tis-drawer-toggle]');
      this.shipping = root.querySelector('[data-tis-shipping]');
      this.backdrop = root.querySelector('[data-tis-backdrop]');
      this.shippingToggles = [
        ...root.querySelectorAll('[data-tis-shipping-toggle]'),
        ...document.querySelectorAll('[data-tis-footer-shipping-toggle]'),
      ];
      this.countryList = root.querySelector('[data-tis-country-list]');
      this.searchDetails = [...root.querySelectorAll('[data-tis-search] details')];
      this.homeLinks = [...root.querySelectorAll('[data-tis-home-link]')];
      this.closeTimer = null;
      this.openTimer = null;
      this.scrollFrame = null;
      this.activeMenu = null;
      this.bind();
      this.updateMeasurements();
      if ('ResizeObserver' in window) {
        this.measureObserver = new ResizeObserver(() => this.updateMeasurements());
        this.measureObserver.observe(this.root);
      }
      this.updateMode();
    }

    bind() {
      this.homeLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          const destination = new URL(link.href, window.location.href);
          const normalizePath = (path) => path.replace(/\/+$/, '') || '/';
          const isCurrentHomepage =
            destination.origin === window.location.origin &&
            normalizePath(destination.pathname) === '/' &&
            normalizePath(window.location.pathname) === '/';

          if (!isCurrentHomepage) return;

          event.preventDefault();
          this.closeLayers();
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      });

      this.triggers.forEach((trigger) => {
        const name = trigger.dataset.tisMenuTrigger;
        trigger.setAttribute('aria-expanded', 'false');
        trigger.addEventListener('mouseenter', () => {
          if (!this.desktop.matches) return;
          window.clearTimeout(this.openTimer);
          window.clearTimeout(this.closeTimer);
          if (this.activeMenu) this.openMenu(name);
          else this.openTimer = window.setTimeout(() => this.openMenu(name), 300);
        });
        trigger.addEventListener('mouseleave', () => window.clearTimeout(this.openTimer));
        trigger.addEventListener('focus', () => {
          if (this.desktop.matches) this.openMenu(name);
        });
      });

      this.root.querySelector('[data-tis-menus]')?.addEventListener('mouseenter', () => {
        window.clearTimeout(this.closeTimer);
      });

      this.root.addEventListener('mouseleave', () => {
        if (!this.desktop.matches) return;
        window.clearTimeout(this.openTimer);
        window.clearTimeout(this.closeTimer);
        this.closeTimer = window.setTimeout(() => this.closeMenu(), 220);
      });

      this.backdrop?.addEventListener('click', () => this.closeLayers());
      this.backdrop?.addEventListener('mouseenter', () => {
        if (!this.desktop.matches) return;
        window.clearTimeout(this.closeTimer);
        this.closeTimer = window.setTimeout(() => this.closeMenu(), 220);
      });

      this.drawerToggle?.addEventListener('click', () => {
        this.root.classList.contains('is-drawer-open') ? this.closeDrawer(true) : this.openDrawer();
      });

      this.root.querySelectorAll('[data-tis-drawer-next]').forEach((button) => {
        button.addEventListener('click', () => this.showDrawerPanel(button.dataset.tisDrawerNext));
      });

      this.root.querySelectorAll('[data-tis-drawer-back]').forEach((button) => {
        button.addEventListener('click', () => this.showDrawerPanel(button.dataset.tisDrawerBack, true));
      });

      this.drawer?.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => this.closeDrawer());
      });

      this.shippingToggles.forEach((button) => {
        button.addEventListener('click', () => {
          this.root.classList.contains('is-shipping-open') ? this.closeShipping(true) : this.openShipping(button);
        });
      });
      this.root.querySelector('[data-tis-shipping-close]')?.addEventListener('click', () => this.closeShipping(true));
      this.root.querySelectorAll('[data-tis-mobile-search]').forEach((button) => {
        button.addEventListener('click', () => {
          this.closeDrawer();
          window.requestAnimationFrame(() => this.root.querySelector('.tis-nav__mobile-search-host summary')?.click());
        });
      });

      this.root.querySelectorAll('[data-tis-cart-link]').forEach((link) => {
        link.addEventListener('click', (event) => {
          const drawer = document.querySelector('cart-drawer');
          if (!drawer || typeof drawer.open !== 'function') return;
          event.preventDefault();
          this.closeLayers();
          drawer.open(link);
        });
      });

      this.searchDetails.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (details.open) {
            this.closeMenu();
            this.closeDrawer();
            this.closeShipping();
          } else {
            details.querySelector('predictive-search')?.close(true);
          }
          document.body.classList.toggle('tis-header-search-open', this.searchDetails.some((item) => item.open));
          this.updateMeasurements();
          this.updateLayerState();
          this.updateMode();
        });
      });

      const announcement = this.root.querySelector('.tis-nav__announcement');
      this.root.querySelector('[data-tis-announcement-close]')?.addEventListener('click', () => {
        this.root.classList.add('is-announcement-hidden');
      });
      announcement?.addEventListener('transitionend', () => this.updateMeasurements());

      document.addEventListener('click', (event) => {
        if (!this.root.classList.contains('is-shipping-open')) return;
        if (event.target.closest('[data-tis-shipping], [data-tis-shipping-toggle], [data-tis-footer-shipping-toggle]')) return;
        this.closeShipping();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeLayers(true);
      });

      window.addEventListener('scroll', () => {
        if (this.scrollFrame !== null) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateMode();
        });
      }, { passive: true });

      window.addEventListener('resize', () => {
        if (this.desktop.matches) this.closeDrawer();
        else this.closeMenu();
        this.updateMeasurements();
        this.updateMode();
      });
    }

    openMenu(name) {
      if (!this.desktop.matches) return;
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      this.closeShipping();
      this.activeMenu = name;
      const activeTrigger = this.triggers.find((trigger) => trigger.dataset.tisMenuTrigger === name);
      const activeMenu = this.menus.find((menu) => menu.dataset.tisMenu === name);
      if (activeTrigger && activeMenu) {
        activeMenu.style.setProperty('--tis-menu-trigger-left', `${activeTrigger.getBoundingClientRect().left}px`);
      }
      this.root.classList.add('is-mega-open');
      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', String(trigger.dataset.tisMenuTrigger === name)));
      this.menus.forEach((menu) => {
        const active = menu.dataset.tisMenu === name;
        menu.classList.toggle('is-active', active);
        menu.setAttribute('aria-hidden', String(!active));
      });
      this.updateLayerState();
      this.updateMode();
    }

    closeMenu() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      this.activeMenu = null;
      this.root.classList.remove('is-mega-open');
      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.menus.forEach((menu) => {
        menu.classList.remove('is-active');
        menu.setAttribute('aria-hidden', 'true');
      });
      this.updateLayerState();
      this.updateMode();
    }

    openDrawer() {
      if (this.desktop.matches) return;
      this.closeShipping();
      this.showDrawerPanel('root');
      this.root.classList.add('is-drawer-open');
      this.drawerToggle?.setAttribute('aria-expanded', 'true');
      this.drawerToggle?.setAttribute('aria-label', 'Close menu');
      this.drawer?.setAttribute('aria-hidden', 'false');
      document.body.classList.add('tis-nav-scroll-lock');
      this.updateLayerState();
      this.updateMode();
    }

    closeDrawer(returnFocus = false) {
      this.root.classList.remove('is-drawer-open');
      this.drawerToggle?.setAttribute('aria-expanded', 'false');
      this.drawerToggle?.setAttribute('aria-label', 'Open menu');
      this.drawer?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('tis-nav-scroll-lock');
      this.showDrawerPanel('root');
      this.updateLayerState();
      this.updateMode();
      if (returnFocus) this.drawerToggle?.focus();
    }

    showDrawerPanel(name, reverse = false) {
      const panels = [...this.root.querySelectorAll('[data-tis-drawer-panel]')];
      panels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.tisDrawerPanel === name);
        panel.classList.toggle('is-reverse', reverse && panel.dataset.tisDrawerPanel === name);
        if (panel.dataset.tisDrawerPanel === name) panel.scrollTop = 0;
      });
    }

    openShipping(trigger) {
      this.closeMenu();
      this.closeDrawer();
      this.shippingReturnFocus = trigger;
      this.root.classList.add('is-shipping-open');
      this.shipping?.setAttribute('aria-hidden', 'false');
      this.shippingToggles.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('tis-nav-scroll-lock');
      this.updateLayerState();
      this.updateMode();
    }

    closeShipping(returnFocus = false) {
      this.root.classList.remove('is-shipping-open');
      this.shipping?.setAttribute('aria-hidden', 'true');
      this.shippingToggles.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('tis-nav-scroll-lock');
      this.updateLayerState();
      this.updateMode();
      if (returnFocus) this.shippingReturnFocus?.focus();
    }

    closeLayers(returnFocus = false) {
      this.closeMenu();
      this.closeDrawer(returnFocus);
      this.closeShipping(returnFocus);
    }

    updateLayerState() {
      const open = this.root.classList.contains('is-mega-open') ||
        this.root.classList.contains('is-drawer-open') ||
        this.root.classList.contains('is-shipping-open') ||
        document.body.classList.contains('tis-header-search-open');
      document.body.classList.toggle('tis-nav-layer-open', open);
    }

    updateMeasurements() {
      const height = this.root.offsetHeight;
      const announcement = this.root.querySelector('.tis-nav__announcement');
      document.documentElement.style.setProperty('--tis-nav-height', `${height}px`);
      document.documentElement.style.setProperty('--tis-header-height', `${height}px`);
      document.documentElement.style.setProperty('--header-height', `${height}px`);
      document.documentElement.style.setProperty(
        '--tis-announcement-height',
        `${announcement?.offsetHeight || 0}px`,
      );
    }

    updateMode() {
      const isHome = document.body.classList.contains('template-index');
      const shippingOpen = this.root.classList.contains('is-shipping-open');
      const layerOpen = document.body.classList.contains('tis-nav-layer-open') ||
        document.body.classList.contains('tis-header-search-open');
      const threshold = this.desktop.matches ? this.desktopThreshold() : MOBILE_THRESHOLD;
      // The country selector is a full-screen opaque surface. Preserve the
      // homepage header mode beneath it so its foreground/background do not
      // swap while the selector fades in or out.
      document.body.classList.toggle(
        'tis-nav-at-top',
        isHome && window.scrollY <= threshold && (!layerOpen || shippingOpen),
      );
    }

    desktopThreshold() {
      const hero = document.querySelector('#MainContent [data-tis-home-hero]');
      const headline = hero?.querySelector('.tis-home-hero__heading');
      const bar = this.root.querySelector('.tis-nav__bar');
      if (headline) {
        const headlineTop = headline.getBoundingClientRect().top + window.scrollY;
        return Math.max(MOBILE_THRESHOLD, headlineTop - this.root.offsetHeight - HEADLINE_GAP);
      }
      return Math.max(MOBILE_THRESHOLD, (hero?.offsetHeight || window.innerHeight) - (bar?.offsetHeight || 0) - HERO_GAP);
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-tis-nav]').forEach((root) => {
      if (root.dataset.tisNavReady === 'true') return;
      root.dataset.tisNavReady = 'true';
      new TISNavigation(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();


/* tis-search-clear 29 Aug ---------------------------------------------
   The theme hides Dawn's reset button on the search results page and in the
   desktop panel, and the results page form is not wrapped in <search-form>,
   so nothing was managing it. Every search input now gets a working clear
   control on both viewports. CSS block: tis-search-clear 29 Aug. */
(function () {
  function sync(input, button) {
    button.classList.toggle('hidden', input.value.trim().length === 0);
  }
  function init() {
    document.querySelectorAll('.search__input').forEach(function (input) {
      var form = input.closest('form');
      if (!form) return;
      var button = form.querySelector('.reset__button');
      if (!button || button.dataset.tisClear === 'on') return;
      button.dataset.tisClear = 'on';
      sync(input, button);
      input.addEventListener('input', function () { sync(input, button); });
      button.addEventListener('click', function (event) {
        event.preventDefault();
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
        sync(input, button);
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* tis-filter-chip-feedback 29 Aug -------------------------------------
   Removing an applied filter reloads the page, so the chip used to sit
   there looking untouched until the new page painted. Capture the click
   first and take the chip away immediately, so the control answers at
   once. The theme\'s own handler still runs and does the navigation. */
document.addEventListener('click', function (event) {
  var target = event.target;
  if (!target || !target.closest) return;
  var chip = target.closest('.tis-filter-applied__chip');
  if (chip) { chip.classList.add('is-removing'); return; }
  var clear = target.closest('.tis-filter-applied__clear');
  if (clear) {
    var wrap = clear.closest('.tis-filter-applied');
    if (wrap) wrap.classList.add('is-clearing');
  }
}, true);


/* tis-search-submit-hold 29 Aug ---------------------------------------
   Submitting a search navigates away. The sheet closed on submit and the
   page behind it flashed up before the results page painted. Hold a white
   ground from submit until the new document arrives. */
(function () {
  var TIMER = null;
  function hold() {
    document.body.classList.add('tis-search-submitting');
    if (TIMER) clearTimeout(TIMER);
    TIMER = setTimeout(release, 4000);
  }
  function release() {
    document.body.classList.remove('tis-search-submitting');
  }
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches) return;
    if (form.matches('form.search') || form.querySelector('.search__input')) hold();
  }, true);
  window.addEventListener('pageshow', release);
  window.addEventListener('pagehide', release);
})();


/* tis-search-one-control 29 Aug ---------------------------------------
   On the phone the sheet close cross is hidden and this one control does
   both jobs: it clears the field while there is a query, and closes the
   sheet once the field is empty. The theme\'s own close button is still in
   the markup, just not shown, so closing goes through its real handler. */
document.addEventListener('click', function (event) {
  var target = event.target;
  if (!target || !target.closest) return;
  var btn = target.closest('.tis-nav__mobile-search-host .reset__button');
  if (!btn) return;
  var modal = btn.closest('.search-modal');
  var input = modal && modal.querySelector('.search__input');
  if (!input || input.value.trim().length > 0) return;
  event.preventDefault();
  event.stopPropagation();
  var close = modal.querySelector('.search-modal__close-button');
  if (close) { close.click(); return; }
  var details = modal.closest('details');
  if (details) details.removeAttribute('open');
}, true);
